import theme from '@/theme';
import { Select } from '@/components/form/Select/Select';
import { FormattedMessage } from '@/components/formatted_message/FormattedMessage';
import type { GetSupplyPointsMetadataResult } from '@/services/api/supplyPoints';
import { Highlighter } from '@/components/highlighter/Highlighter';
import styled from 'styled-components';
import { removeAccents } from '@/utils/string/removeAccents';
import type { SerializedSupplyPointByView, ViewSupplyPoints } from '@enmon/commons/lib/enmon';
import { groupBy } from 'ramda';
import { SupplyPoints as messages } from '../../messages';
import { truncateText } from '@/utils/string/truncate';
import { isCommodity, type Commodity } from '@enmon/commons';
import { getCommodityIndex } from '@/utils/commodities';
import { COMMODITY_TRANSLATED } from '@/common/messages';
import { renderOptionLabelWithIdTag } from './renderOptionLabelWithIdTag';
import { Fragment } from 'react';

const StyledDetails = styled.div`
  font-size: 12px;
  font-weight: ${theme.fonts.body.weight};
  margin-top: ${theme.spacing[3]};
`;

const renderSupplyPointOption = (
  { _id, name, code, contract, registrationNumber }: SerializedSupplyPointByView[ViewSupplyPoints.Dropdown],
  search?: string,
) => {
  const detailParts = [
    code && (
      <Fragment key="code">
        <FormattedMessage {...messages.code} />:{' '}
        <Highlighter highlightedText={search} originalText={truncateText(code ?? '', 18)} />
      </Fragment>
    ),
    contract && (
      <Fragment key="contract">
        <FormattedMessage {...messages.contract} />:{' '}
        <Highlighter highlightedText={search} originalText={truncateText(contract ?? '', 18)} />
      </Fragment>
    ),
  ].filter((part): part is JSX.Element => Boolean(part));

  return (
    <Select.Option key={_id} label={name} title={name} value={_id}>
      {renderOptionLabelWithIdTag(registrationNumber ?? code, name ?? '', search)}
      {detailParts.length > 0 && (
        <StyledDetails>{detailParts.flatMap((part, index) => (index > 0 ? [', ', part] : [part]))}</StyledDetails>
      )}
    </Select.Option>
  );
};

const normalizeForSearch = (value: string): string => removeAccents(value).toLowerCase();

const matchesSearch = (
  supplyPoint: SerializedSupplyPointByView[ViewSupplyPoints.Dropdown],
  search: string,
): boolean => {
  const searchLower = normalizeForSearch(search.trim());
  if (!searchLower) return true;

  const { name, code, contract, registrationNumber } = supplyPoint;
  return (
    normalizeForSearch(name).includes(searchLower) ||
    Boolean(code && normalizeForSearch(code).includes(searchLower)) ||
    Boolean(contract && normalizeForSearch(contract).includes(searchLower)) ||
    Boolean(registrationNumber && normalizeForSearch(registrationNumber).includes(searchLower))
  );
};

const filterSupplyPoints = (
  supplyPoints: SerializedSupplyPointByView[ViewSupplyPoints.Dropdown][],
  search?: string,
): SerializedSupplyPointByView[ViewSupplyPoints.Dropdown][] => {
  return search ? supplyPoints.filter(sp => matchesSearch(sp, search)) : supplyPoints;
};

const getSortedSupplyPointCommodities = (
  groupedSupplyPoints: Record<Commodity, SerializedSupplyPointByView[ViewSupplyPoints.Dropdown][]>,
): Commodity[] => {
  return Object.keys(groupedSupplyPoints)
    .filter(isCommodity)
    .sort((commodityA, commodityB) => getCommodityIndex(commodityA) - getCommodityIndex(commodityB));
};

const renderGroupedOptions = (
  groupedSupplyPoints: Record<Commodity, SerializedSupplyPointByView[ViewSupplyPoints.Dropdown][]>,
  groups: Commodity[],
  search?: string,
): JSX.Element[] => {
  return groups.flatMap(group => [
    <Select.OptGroup key={group} label={<FormattedMessage {...COMMODITY_TRANSLATED[group]} />} />,
    ...groupedSupplyPoints[group].map(supplyPoint => renderSupplyPointOption(supplyPoint, search)),
  ]);
};

const renderUngroupedOptions = (
  supplyPoints: SerializedSupplyPointByView[ViewSupplyPoints.Dropdown][],
  search?: string,
): JSX.Element[] => {
  return supplyPoints.map(supplyPoint => renderSupplyPointOption(supplyPoint, search));
};

export const getSupplyPointsSelectOptions = (
  supplyPointsMetadataResponse?: GetSupplyPointsMetadataResult<ViewSupplyPoints.Dropdown>,
  search?: string,
  groupByCommodity?: boolean,
): JSX.Element[] | undefined => {
  if (!supplyPointsMetadataResponse) return [];

  const filteredResults = filterSupplyPoints(supplyPointsMetadataResponse.result, search);

  if (groupByCommodity) {
    const groupedSupplyPoints = groupBy(({ commodity }) => commodity, filteredResults);
    const sortedGroups = getSortedSupplyPointCommodities(groupedSupplyPoints);
    return renderGroupedOptions(groupedSupplyPoints, sortedGroups, search);
  }

  return renderUngroupedOptions(filteredResults, search);
};
